import _ from "lodash";
import Backbone from "backbone";
import Promise from "bluebird";
import Velocity from "velocity-animate";
import BaseTileView from "scripts/views/baseTileView";
import ProgressBarView from "scripts/views/progressBarView";
import ProgressBarModel from "scripts/models/progressBarModel";

const chan = Backbone.Radio.channel;

class BaseMediaSalvattoreTileView extends BaseTileView {
  constructor(options, securityService) {
    super(options);

    this.securityService = securityService;

    this.isHoverState = false;
    this.isAnimatingToDefaultState = false;
    this.isAnimatingToHoverState = false;
  }

  get className() {
    return "bb-media-salvattore-tile";
  }

  get events() {
    return _.extend({}, super.events, {
      mouseenter: "onMouseEnterTile",
      "click .btn-dismiss-overlay": "animateDefaultState",
      click: "onTouchEndTile",
      "click .bb-salvattore-tile-title-button": "onTitleButtonClick",
    });
  }

  initializeBookshelfDeps() {
    super.initializeBookshelfDeps();

    this.tileProgressBarModel = new ProgressBarModel({
      showCancelButton: false,
    });

    this.tileProgressBarView = this.addSubView(
      new ProgressBarView({
        className: "bb-download-indicator-container tile-version",
        model: this.tileProgressBarModel,
      }),
    );

    this.tileHoverProgressBarModel = new ProgressBarModel();

    this.tileHoverProgressBarView = this.addSubView(
      new ProgressBarView({
        className: "bb-download-indicator-container tile-hover-version",
        model: this.tileHoverProgressBarModel,
      }),
    );

    this.tileHoverProgressBarView.on(
      "progressBarCancel",
      () => this.onCancelBookshelfDownload(),
      this,
    );
  }

  render() {
    const user = this.securityService.getUser();
    const modelData = this.model.toJSON();

    const templateData = _.merge(modelData, {
      viewerUrl: this.getViewerUrl(modelData.id),
      showBadges: user.getShowBadges(),
      downloadUrl: this.getDownloadUrl(),
    });

    this.$el.html(this.template(templateData));

    this.initDetailButton();
    this.initFavoriteButton();
    this.initCurationAddButton();
    this.initTooltips();

    if (this.securityService.getUser().get("bot")) {
      this.animateHoverState(0);
    }

    this.initializeDownloadButton();

    this.initializeCurationMultiSelect();

    return this;
  }

  renderBookshelfElements() {
    super.renderBookshelfElements();

    this.tileHoverProgressBarView
      .htmlOf(this.$el.find(".tile-hover .bb-download-progress-bar-region"))
      .render();

    this.tileProgressBarView
      .htmlOf(
        this.$el.find(
          ".media-image-container .bb-download-progress-bar-region",
        ),
      )
      .render();
  }

  hasTouchMoved() {
    return chan("display").request("touch:touchMoved");
  }

  isScrolling() {
    return chan("display").request("scroll:scrolling");
  }

  onMouseEnterTile() {
    if (!this.isScrolling()) {
      if (this.curationMultiSelectService.getActive()) {
        // because of the way the salvatore tiles works regular css did NOT work here
        this.$el.css("cursor", "pointer");
        this.$el.one("mouseleave", () => {
          this.$el.css("cursor", "default");
        });
      } else {
        const animateDelay = setTimeout(() => {
          this.animateHoverState();
        }, 200);

        this.$el.one("mouseleave", () => {
          clearTimeout(animateDelay);
          this.animateDefaultState();
        });
      }
    }
  }

  /**
   * A tile has broadcast it's going to hover state from a touch end event.
   */
  onTouchEndHoverState(tile) {
    if (this !== tile) {
      this.animateDefaultState();
    }
  }

  onTouchEndTile(event) {
    if (this.curationMultiSelectService.getActive()) {
      this.onClickCurationMultiSelectItem();
      return;
    }

    // Did user touch (click) on a button in the tile?
    const linkTouched = $(event.target).is("a, a > span, a > i");

    if (!linkTouched) {
      event.preventDefault();

      if (!this.hasTouchMoved()) {
        if (!this.isHoverState) {
          chan("salvattoreTile").on(
            "touchEndHoverState",
            this.onTouchEndHoverState,
            this,
          );

          chan("salvattoreTile").trigger("touchEndHoverState", this);

          /**
           * Sometimes more than one tile can end up in the hover state
           * when tapping rapidly. This is a cheap fix to make sure other
           * tiles change back to default state after animations to hover
           * state complete without having to track state on other tiles.
           */
          this.animateHoverState().then(() =>
            chan("salvattoreTile").trigger("touchEndHoverState", this),
          );
        } else {
          this.animateDefaultState();
        }
      }
    }
  }

  onTitleButtonClick() {
    this.animateHoverState(0);

    setTimeout(() => {
      this.$el.find(".bb-salvattore-tile-view-link").focus();
    }, 100);
  }

  animateHoverState(duration = 200) {
    if (
      (!this.isHoverState || this.isAnimatingToDefaultState) &&
      !this.isAnimatingToHoverState
    ) {
      this.isAnimatingToHoverState = true;

      const tile = this.el;
      const $tile = this.$el;
      const elementsToFade = tile.querySelectorAll(
        ".btn-block, .tile-overlay, .tile-hover-header, .parent-anthology",
      );
      if (_.isEmpty(elementsToFade)) {
        // this seems to occur sometimes when leaving the page
        // no elements are found
        return Promise.resolve();
      }

      const tileHeight = $tile.height();
      const overlayHeight = tileHeight;
      const overlayWidth = $tile.width();
      const overlayMarginTop = -overlayHeight / 2;
      const overlayMarginLeft = -overlayWidth / 2;

      // allows for vertical centering at various heights
      tile.querySelector(".tile-hover").style.height = tileHeight + "px";

      tile.querySelector(".tile-overlay").style.width = overlayWidth + "px";
      tile.querySelector(".tile-overlay").style.height = overlayHeight + "px";
      tile.querySelector(".tile-overlay").style.marginTop =
        overlayMarginTop + "px";
      tile.querySelector(".tile-overlay").style.marginLeft =
        overlayMarginLeft + "px";

      return Velocity.animate(elementsToFade, "transition.fadeIn", {
        duration: duration,
      }).then(() => {
        this.isHoverState = true;
        this.isAnimatingToHoverState = false;
        this.isAnimatingToDefaultState = false;
      });
    }

    return Promise.resolve();
  }

  animateDefaultState() {
    if (
      (this.isHoverState || this.isAnimatingToHoverState) &&
      !this.isAnimatingToDefaultState
    ) {
      this.isAnimatingToDefaultState = true;

      const tile = this.el;
      const elementsToFade = tile.querySelectorAll(
        ".btn-block, .tile-overlay, .tile-hover-header, .parent-anthology",
      );

      return Velocity.animate(elementsToFade, "transition.fadeOut", {
        duration: 350,
      }).then(() => {
        this.isHoverState = false;
        this.isAnimatingToDefaultState = false;
        this.isAnimatingToHoverState = false;
        chan("salvattoreTile").off(
          "touchEndHoverState",
          this.onTouchEndHoverState,
          this,
        );
      });
    }

    return Promise.resolve();
  }

  updateProgressBar(percent) {
    this.tileHoverProgressBarModel.set("isVisible", true);
    this.tileProgressBarModel.set("isVisible", true);

    this.tileHoverProgressBarModel.set("percent", percent);
    this.tileProgressBarModel.set("percent", percent);
  }

  hideProgressBar() {
    this.tileHoverProgressBarModel.set("isVisible", false);
    this.tileProgressBarModel.set("isVisible", false);
  }
}

export default BaseMediaSalvattoreTileView;
